import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { apiConfig, Theme } from '@constants';

import { MapTileLayer } from './components/MapTileLayer';
import { AlertType, MapAlertTileLayer } from './MapAlertTileLayer';
import { MapLightningLayer } from './MapLightningLayer';
import { MapPlottedValuesLayer } from './MapPlottedValuesLayer';
import { OverlayType, OverlayTypeInfo } from './MapTypes';

const tPath = 'map.';

const tileURL = (overlayType, timeSlice, forecastTimeSlice) => {
  const overlayKey = OverlayTypeInfo[overlayType].inventoryKey;

  const ftsParameter = forecastTimeSlice ? `&fts=${forecastTimeSlice}` : '';

  return `${apiConfig.TWC_API_MAP_TILES_URL}/${overlayKey}?ts=${timeSlice}${ftsParameter}&xyz={x}:{y}:{z}&apiKey=${apiConfig.TWC_API_MAP_TILE_KEY}`;
};

const RadarOverlay = forwardRef(({ onOverlayReady, timeSlice, alpha, center, zoom }, reference) => (
  <MapTileLayer
    key={`RadarOverlay-${timeSlice}`}
    alpha={alpha}
    center={center}
    ref={reference}
    uriTemplate={tileURL(OverlayType.radar, timeSlice)}
    zoom={zoom}
    onTilesLoaded={onOverlayReady}
  />
));

const FutureRadarOverlay = forwardRef(
  ({ onOverlayReady, timeSlice, forecastTimeSlice, alpha, center, zoom }, reference) => (
    <MapTileLayer
      key={`FutureRadarOverlay-${timeSlice}`}
      alpha={alpha}
      center={center}
      ref={reference}
      uriTemplate={tileURL(OverlayType.futureRadar, timeSlice, forecastTimeSlice)}
      zoom={zoom}
      onTilesLoaded={onOverlayReady}
    />
  ),
);

const LightningOverlay = forwardRef(({ alpha, center, zoom }, reference) => (
  <MapLightningLayer.API key={'LightingBolts'} alpha={alpha} center={center} zoom={zoom} />
));

const AlertsOverlay = forwardRef(({ alpha, center, onOverlayReady, zoom }, reference) => (
  <View ref={reference} style={styles.overlayView}>
    <MapAlertTileLayer.CompleteAPI
      key={'AlertsOverlayComplete'}
      alpha={alpha}
      center={center}
      zoom={zoom}
      onFinishedDrawing={() => {
        onOverlayReady();
      }}
    />
  </View>
));

const SevereAlertsOverlay = forwardRef(({ alpha, center, zoom }, reference) => (
  <View ref={reference} style={styles.overlayView}>
    <MapAlertTileLayer.SevereAPI
      key={'AlertsOverlaySevere'}
      alpha={alpha}
      center={center}
      showAlertTypes={[AlertType.thunderstormWarning, AlertType.tornadoWarning, AlertType.flashFloodWarning]}
      zoom={zoom}
    />
  </View>
));

const SatelliteOverlay = forwardRef(({ onOverlayReady, timeSlice, alpha, center, zoom }, reference) => (
  <MapTileLayer
    key={`SatelliteOverlay-${timeSlice}`}
    alpha={alpha}
    center={center}
    ref={reference}
    uriTemplate={tileURL(OverlayType.satellite, timeSlice)}
    zoom={zoom}
    onTilesLoaded={onOverlayReady}
  />
));

const TemperatureOverlay = forwardRef(({ onOverlayReady, timeSlice, alpha, center, zoom }, reference) => (
  <MapTileLayer
    key={`TemperatureOverlay-${timeSlice}`}
    alpha={alpha}
    center={center}
    ref={reference}
    uriTemplate={tileURL(OverlayType.temperature, timeSlice)}
    zoom={zoom}
    onTilesLoaded={onOverlayReady}
  />
));

const FeelsLikeOverlay = forwardRef(({ onOverlayReady, timeSlice, alpha, center, zoom }, reference) => (
  <MapTileLayer
    key={`FeelsLikeOverlay-${timeSlice}`}
    alpha={alpha}
    center={center}
    ref={reference}
    uriTemplate={tileURL(OverlayType.feelsLike, timeSlice)}
    zoom={zoom}
    onTilesLoaded={onOverlayReady}
  />
));

const WindOverlay = forwardRef(({ onOverlayReady, timeSlice, alpha, center, zoom }, reference) => (
  <MapTileLayer
    key={`WindOverlay-${timeSlice}`}
    alpha={alpha}
    center={center}
    ref={reference}
    uriTemplate={tileURL(OverlayType.wind, timeSlice)}
    zoom={zoom}
    onTilesLoaded={onOverlayReady}
  />
));

const BreathingIndexOverlay = forwardRef(
  ({ onOverlayReady, timeSlice, forecastTimeSlice, alpha, center, zoom }, reference) => (
    <MapTileLayer
      key={`BreathingIndexOverlay-${timeSlice}`}
      alpha={alpha}
      center={center}
      ref={reference}
      uriTemplate={tileURL(OverlayType.breathingIndex, timeSlice, forecastTimeSlice)}
      zoom={zoom}
      onTilesLoaded={onOverlayReady}
    />
  ),
);

// Provides a map between a string overlay type and a component to represent that overlay.
const OverlayTypeComponents = {
  radar: RadarOverlay,
  futureRadar: FutureRadarOverlay,
  lightning: LightningOverlay,
  alerts: AlertsOverlay,
  severeAlerts: SevereAlertsOverlay,
  satellite: SatelliteOverlay,
  temperature: TemperatureOverlay,
  feelsLike: FeelsLikeOverlay,
  wind: WindOverlay,
  breathingIndex: BreathingIndexOverlay,
};

// Returns an overlay matching the OverlayType provided.
export const Overlay = forwardRef(
  ({ overlayType, timeSlice, forecastTimeSlice, frameIndex, alpha, center, onOverlayReady, zoom }, reference) => {
    const UseOverlay = OverlayTypeComponents[overlayType];

    return (
      <UseOverlay
        key={`overlay-${frameIndex}`}
        alpha={alpha}
        center={center}
        forecastTimeSlice={forecastTimeSlice}
        ref={reference}
        timeSlice={timeSlice}
        zoom={zoom}
        onOverlayReady={onOverlayReady}
      />
    );
  },
);

// Overlay.propTypes = {
//   overlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   timeSlice: PropTypes.number,
//   forecastTimeSlice: PropTypes.number,
//   frameIndex: PropTypes.number,
//   alpha: PropTypes.number,
//   center: PropTypes.arrayOf(PropTypes.number).isRequired,
//   onOverlayReady: PropTypes.func,
//   zoom: PropTypes.number.isRequired,
// };

Overlay.defaultProps = {
  timeSlice: null,
  forecastTimeSlice: null,
  frameIndex: null,
  alpha: 1,
  onOverlayReady: () => {},
};

// Returns any promoted overlays that should be shown above other map layers for a given overlay type.
const PromotedOverlayUI = forwardRef(({ onOverlayReady, overlayType, alpha, center, zoom }, reference) => {
  // Note:  When adding an additional promoted overlay, be sure to update hasPromotedOverlay below.
  switch (overlayType) {
    case OverlayType.temperature:
    case OverlayType.feelsLike:
    case OverlayType.wind:
      return (
        <MapPlottedValuesLayer.API
          key={'PlottedValueOverlay'}
          alpha={alpha}
          center={center}
          overlayType={overlayType}
          ref={reference}
          zoom={zoom}
          onOverlayReady={onOverlayReady}
        />
      );
    default:
      return null;
  }
});

export const PromotedOverlay = {
  UI: PromotedOverlayUI,
  hasPromotedOverlay: overlayType => {
    switch (overlayType) {
      case OverlayType.temperature:
      case OverlayType.feelsLike:
      case OverlayType.wind:
        // These three overlay types have value plot overlays.  Currently, we have
        // decided to disable the plots because the text interferes with the city
        // names in the GIS layer.  Once a new GIS layer has been created, we can
        // enable the value plot layers again.  To disable the plot layers,
        // we are returning false instead of true here.
        return false;
      default:
        return false;
    }
  },
};

// PromotedOverlayUI.propTypes = {
//   overlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   alpha: PropTypes.number,
//   center: PropTypes.arrayOf(PropTypes.number).isRequired,
//   onOverlayReady: PropTypes.func,
//   zoom: PropTypes.number.isRequired,
// };

PromotedOverlayUI.defaultProps = {
  alpha: 1,
  onOverlayReady: () => {},
};

// Returns a localized title for the specified overlay type.
export const OverlayTypeTitle = ({ overlayType }) => {
  const { t } = useTranslation();

  return t(`${tPath}overlayTitle.${overlayType}`);
};

// OverlayTypeTitle.propTypes = {
//   overlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
// };

const styles = StyleSheet.create({
  overlayView: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    overflow: 'hidden',
    position: 'absolute',
    right: Theme.px(0),
    top: Theme.px(0),
  },
});
